// Cierge Labs v1 — Client-side wallet (WebCrypto, ECDSA P-256).
// Keys are generated and held entirely in the browser. Only the
// public-key fingerprint is ever transmitted to the backend, and only
// when the visitor explicitly saves a draft.

const STORAGE_KEY = "cierge.wallet.v1";

const buf2hex = (buf) =>
  [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

async function fingerprintFromJwk(publicJwk) {
  const canonical = JSON.stringify({
    crv: publicJwk.crv,
    kty: publicJwk.kty,
    x: publicJwk.x,
    y: publicJwk.y,
  });
  const data = new TextEncoder().encode(canonical);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return buf2hex(digest);
}

export async function generateWallet(handle) {
  const keyPair = await crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign", "verify"],
  );
  const publicJwk = await crypto.subtle.exportKey("jwk", keyPair.publicKey);
  const privateJwk = await crypto.subtle.exportKey("jwk", keyPair.privateKey);
  const fingerprint = await fingerprintFromJwk(publicJwk);

  const wallet = {
    handle: (handle || "anonymous").trim() || "anonymous",
    fingerprint,
    publicJwk,
    privateJwk,
    createdAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(wallet));
  return wallet;
}

export function getWallet() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearWallet() {
  localStorage.removeItem(STORAGE_KEY);
}

export function shortFingerprint(fp) {
  if (!fp) return "";
  return `${fp.slice(0, 8)}…${fp.slice(-6)}`;
}

// Sign an arbitrary payload string. Returns hex signature.
export async function signPayload(payload) {
  const wallet = getWallet();
  if (!wallet) throw new Error("No wallet present");
  const privateKey = await crypto.subtle.importKey(
    "jwk",
    wallet.privateJwk,
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign"],
  );
  const data = new TextEncoder().encode(payload);
  const sig = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    privateKey,
    data,
  );
  return buf2hex(sig);
}
